import { useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { CellValueChangedEvent, ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import Tabs from '../Tabs/Tabs';
import { MultiSelectCellEditor } from '../AgGrid/CellEditors/MultiSelectCellEditor';
import { WithTemplates } from '../../../../types/models/point-style.model';
import { LineStyleApiModel } from '../../../../types/models/line-style.model';
import { CreateAutodeskLayerApiModel } from '../../../../types/models/sync/autodesk-layer-sync-plan.model';
import { CreatePointStyleApiModel } from '../../../../types/models/sync/point-style-sync-plan.model';
import { CreateCivil3dPropertyApiModel } from '../../../../types/models/sync/civil3d-property-sync-plan.model';
import { CreatePointLabelStyleApiModel } from '../../../../types/models/sync/point-label-style-sync-plan.model';
import { CreateLineStyleApiModel } from '../../../../types/models/sync/line-style-sync-plan.model';
import { CreateCivil3dPropertySetApiModel } from '../../../../types/models/sync/civil3d-property-set-sync-plan.model';
import { SyncPlan, SyncTemplatePlanStatus } from '../../../../types/constants/sync';

type WithStatus = { status: SyncTemplatePlanStatus; lastDateModified?: Date };

type Props = {
  errors?: string[];
  layers: (CreateAutodeskLayerApiModel & WithStatus)[];
  lineStyles: (CreateLineStyleApiModel & WithStatus)[];
  pointLabelStyles: (CreatePointLabelStyleApiModel & WithStatus)[];
  pointStyles: (CreatePointStyleApiModel & WithStatus)[];
  propertySets: (CreateCivil3dPropertySetApiModel & WithStatus)[];
  userDefinedProperties: (CreateCivil3dPropertyApiModel & WithStatus)[];
  onRowChange(plan: SyncPlan, event: CellValueChangedEvent): void;
  templateIdNameMap: Record<string, string>;
  templateOptions: {
    label: string;
    value: number | string | undefined;
  }[];
  loading: boolean;
};

const TemplateSync = ({
  errors,
  layers,
  lineStyles,
  pointLabelStyles,
  pointStyles,
  propertySets,
  userDefinedProperties,
  onRowChange,
  templateIdNameMap,
  templateOptions,
  loading,
}: Props) => {
  const [selectedTab, setSelectedTab] = useState(0);

  const handleFormatTemplatesValue = (params: ValueFormatterParams<LineStyleApiModel & WithStatus>) => {
    if (!params.value || !Array.isArray(params.value) || params.value.length === 0) {
      return '';
    }

    return params.value.map((item: number | string) => templateIdNameMap[item] ?? item).join(', ');
  };

  const handleFormatTemplateEdit = (params: { value: number }) => templateIdNameMap?.[params.value] ?? '';

  const handleFormatTemplateValue = (params: { value: number }) => templateIdNameMap?.[params.value] ?? '';

  const handleGetTemplateValue = ({ data }: ValueGetterParams) => data?.template;

  const lineStyleColumns: ColDef<CreateLineStyleApiModel & WithStatus>[] = [
    {
      field: 'name',
      valueGetter: ({ data }) => data?.name,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'status',
      maxWidth: 120,
      valueGetter: ({ data }) => data?.status,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Current Date',
      field: 'lastDateModified',
      valueGetter: ({ data }) => data?.lastDateModified,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'New Date',
      field: 'dateModified',
      valueGetter: ({ data }) => data?.dateModified,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Template(s)',
      field: 'templates',
      minWidth: 250,
      flex: 1,
      editable: (params) => params.data?.status !== SyncTemplatePlanStatus.REMOVED,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: MultiSelectCellEditor,
      cellEditorParams: {
        options: templateOptions,
      },
      valueFormatter: handleFormatTemplatesValue,
    },
  ];

  const layerColumns: ColDef<CreateAutodeskLayerApiModel & WithStatus>[] = [
    {
      field: 'name',
      valueGetter: ({ data }) => data?.name,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'status',
      maxWidth: 120,
      valueGetter: ({ data }) => data?.status,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Template',
      field: 'template',
      minWidth: 250,
      flex: 1,
      valueGetter: handleGetTemplateValue,
      editable: (params) => params.data?.status !== SyncTemplatePlanStatus.REMOVED,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: templateOptions.map(({ value }) => value),
        formatValue: handleFormatTemplateEdit,
      },
      valueFormatter: handleFormatTemplateValue,
    },
  ];

  const pointLabelStyleColumns: ColDef<CreatePointLabelStyleApiModel & WithStatus>[] = [
    {
      field: 'name',
      valueGetter: ({ data }) => data?.name,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'status',
      maxWidth: 120,
      valueGetter: ({ data }) => data?.status,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Current Date',
      field: 'lastDateModified',
      valueGetter: ({ data }) => data?.lastDateModified,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'New Date',
      field: 'dateModified',
      valueGetter: ({ data }) => data?.dateModified,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Template(s)',
      field: 'templates',
      minWidth: 250,
      flex: 1,
      editable: (params) => params.data?.status !== SyncTemplatePlanStatus.REMOVED,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: MultiSelectCellEditor,
      cellEditorParams: {
        options: templateOptions,
      },
      valueFormatter: handleFormatTemplatesValue,
    },
  ];

  const pointStyleColumns: ColDef<CreatePointStyleApiModel & WithStatus>[] = [
    {
      field: 'name',
      valueGetter: ({ data }) => data?.name,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'status',
      maxWidth: 120,
      valueGetter: ({ data }) => data?.status,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Current Date',
      field: 'lastDateModified',
      valueGetter: ({ data }) => data?.lastDateModified,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'New Date',
      field: 'dateModified',
      valueGetter: ({ data }) => data?.dateModified,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Template(s)',
      field: 'templates',
      minWidth: 250,
      flex: 1,
      editable: (params) => params.data?.status !== SyncTemplatePlanStatus.REMOVED,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: MultiSelectCellEditor,
      cellEditorParams: {
        options: templateOptions,
      },
      valueFormatter: handleFormatTemplatesValue,
    },
  ];

  const propertySetColumns: ColDef<CreateCivil3dPropertyApiModel & WithTemplates & WithStatus>[] = [
    {
      field: 'name',
      valueGetter: ({ data }) => data?.name,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'status',
      maxWidth: 120,
      valueGetter: ({ data }) => data?.status,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Template',
      field: 'template',
      minWidth: 250,
      flex: 1,
      valueGetter: handleGetTemplateValue,
      editable: (params) => params.data?.status !== SyncTemplatePlanStatus.REMOVED,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: templateOptions.map(({ value }) => value),
        formatValue: handleFormatTemplateEdit,
      },
      valueFormatter: handleFormatTemplateValue,
    },
  ];

  const userDefinedPropertyColumns: ColDef<CreateCivil3dPropertyApiModel & WithTemplates & WithStatus>[] = [
    {
      field: 'name',
      valueGetter: ({ data }) => data?.name,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'status',
      maxWidth: 120,
      valueGetter: ({ data }) => data?.status,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Template',
      field: 'template',
      minWidth: 250,
      flex: 1,
      valueGetter: handleGetTemplateValue,
      editable: (params) => params.data?.status !== SyncTemplatePlanStatus.REMOVED,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: templateOptions.map(({ value }) => value),
        formatValue: handleFormatTemplateEdit,
      },
      valueFormatter: handleFormatTemplateValue,
    },
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const tabDefinitions = [
    {
      plan: SyncPlan.LINE_STYLE_SYNC_PLAN,
      label: 'FEATURE LINE STYLES',
      rowData: lineStyles,
      columns: lineStyleColumns,
    },
    { plan: SyncPlan.AUTODESK_LAYER_SYNC_PLAN, label: 'LAYERS', rowData: layers, columns: layerColumns },
    {
      plan: SyncPlan.POINT_LABEL_STYLE_SYNC_PLAN,
      label: 'POINT LABEL STYLES',
      rowData: pointLabelStyles,
      columns: pointLabelStyleColumns,
    },
    { plan: SyncPlan.POINT_STYLE_SYNC_PLAN, label: 'POINT STYLES', rowData: pointStyles, columns: pointStyleColumns },
    {
      plan: SyncPlan.CIVIL3D_PROPERTY_SET_SYNC_PLAN,
      label: 'PROPERTY SETS',
      rowData: propertySets,
      columns: propertySetColumns,
    },
    {
      plan: SyncPlan.CIVIL3D_PROPERTY_SYNC_PLAN,
      label: 'PROPERTIES',
      rowData: userDefinedProperties,
      columns: userDefinedPropertyColumns,
    },
  ];

  const tabs = tabDefinitions.map(({ plan, label, rowData, columns }) => ({
    label,
    content: (
      <Box sx={{ width: '100%', height: 400 }}>
        <AgGridReact
          columnDefs={columns}
          rowData={rowData as unknown[]}
          defaultColDef={{ flex: 1, minWidth: 100, filter: true }}
          getRowId={(params) => (params.data as { name: string }).name}
          loading={loading}
          onCellValueChanged={(event) => onRowChange(plan, event)}
        />
      </Box>
    ),
  }));

  return (
    <Box>
      {(errors ?? []).length > 0 && (
        <Box sx={{ maxHeight: '12.5rem', overflow: 'auto', mb: 2 }}>
          <Alert severity="error">
            {errors?.map((error, index) => (
              <Typography key={index} color="error.main" sx={{ mt: 0 }}>
                {error}
              </Typography>
            ))}
          </Alert>
        </Box>
      )}
      <Tabs tabs={tabs} value={selectedTab} onChange={handleTabChange} />
    </Box>
  );
};

export default TemplateSync;
